import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Check, Download, X, FolderOpen, Trash2 } from 'lucide-react';
import { API } from '../../content/data';
import { FONT_PRESETS } from '../../lib/injectors';

const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';
const labelCls = 'overline !text-[10px] mb-1.5 block text-navy/50';

function useAdminFetch(key, onAuthError) {
  return useCallback(async (fn) => {
    try {
      return await fn(axios.create({ headers: { 'X-Admin-Key': key } }));
    } catch (e) {
      if (e.response?.status === 401) onAuthError();
      throw e;
    }
  }, [key, onAuthError]);
}

function SectionHead({ title, desc }) {
  return (
    <div className="mb-6">
      <h2 className="text-h3 font-semibold text-navy">{title}</h2>
      {desc && <p className="mt-1 text-sm text-navy/55">{desc}</p>}
    </div>
  );
}

/* ---------------- Media Picker (browse & pick) ---------------- */
export function MediaPicker({ adminKey, kinds, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/media`, { headers: { 'X-Admin-Key': adminKey } })
      .then((r) => setItems(r.data.filter((m) => !kinds || kinds.includes(m.kind))))
      .catch(() => {});
  }, [open, adminKey, kinds]);
  return (
    <>
      <button type="button" data-testid={testId} onClick={() => setOpen(true)} className="mt-2 flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-electric hover:text-electric">
        <FolderOpen className="h-3.5 w-3.5" /> Browse Library
      </button>
      {open && (
        <div data-testid="media-picker-modal" className="fixed inset-0 z-[95] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-paper p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-h3 font-semibold text-navy">Choose from Media Library</h3>
              <button onClick={() => setOpen(false)} className="flex touch-target items-center justify-center text-navy/50 hover:text-navy" aria-label="Close picker"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {items.map((m) => (
                <button key={m.id} data-testid={`picker-item-${m.id.slice(0, 8)}`} onClick={() => { onPick(m.url); setOpen(false); }} className="group overflow-hidden rounded-2xl border border-navy/10 bg-white text-left transition-colors hover:border-electric">
                  <div className="flex h-24 items-center justify-center bg-paper">
                    {m.kind === 'image' ? <img src={m.url} alt={m.original} className="h-full w-full object-cover" /> : <span className="font-mono text-[11px] uppercase text-navy/40">{m.kind}</span>}
                  </div>
                  <p className="truncate px-3 py-2 text-[11px] font-semibold text-navy group-hover:text-electric">{m.original}</p>
                </button>
              ))}
              {items.length === 0 && <p className="col-span-full py-10 text-center text-sm text-navy/50">No matching files — upload one in the Media Library first.</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Design & Theme ---------------- */
const COLOR_FIELDS = [
  ['colorNavy', 'Dark sections & headings (navy)', '#061E29'],
  ['colorAccent', 'Accent, links & primary buttons (electric blue)', '#1B2CC1'],
  ['colorGold', 'Highlight & secondary buttons (gold)', '#FCE59A'],
  ['colorPaper', 'Light background (paper)', '#F5F5F5'],
];

export function DesignSection({ adminKey, onAuthError }) {
  const empty = { colorNavy: '', colorAccent: '', colorGold: '', colorPaper: '', fontPreset: '', customFontUrl: '', fontScale: '' };
  const [form, setForm] = useState(empty);
  const [themes, setThemes] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [saved, setSaved] = useState(false);
  const run = useAdminFetch(adminKey, onAuthError);
  const loadThemes = useCallback(() => run((c) => c.get(`${API}/admin/themes`)).then((r) => setThemes(r.data)).catch(() => {}), [run]);
  useEffect(() => {
    run((c) => c.get(`${API}/settings`)).then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
    loadThemes();
  }, [run, loadThemes]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    await run((c) => c.put(`${API}/admin/settings`, form));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const savePreset = async () => {
    if (!presetName.trim()) return;
    await run((c) => c.post(`${API}/admin/themes`, { name: presetName.trim(), values: form }));
    setPresetName('');
    loadThemes();
  };
  const applyPreset = async (t) => {
    await run((c) => c.put(`${API}/admin/settings`, t.values));
    setForm((f) => ({ ...empty, ...f, ...t.values }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div data-testid="admin-design-section">
      <SectionHead title="Design & Theme" desc="Recolor the site, change typography and scale — no code needed. Clear a field to restore the brand default." />

      {/* Theme presets */}
      <div className="mb-6 max-w-3xl rounded-3xl bg-navy p-6 text-white">
        <h3 className="text-lg font-semibold">Theme Presets</h3>
        <p className="mt-1 text-sm text-white/60">Save the current look as a named preset and switch the whole site in one click.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <input data-testid="preset-name-input" className="min-w-[200px] flex-1 rounded-xl border border-white/15 bg-navy-800 px-4 py-3 text-sm text-white outline-none focus:border-gold placeholder:text-white/35" placeholder="Preset name (e.g. Diwali Campaign)" value={presetName} onChange={(e) => setPresetName(e.target.value)} />
          <button data-testid="preset-save-button" onClick={savePreset} className="rounded-full bg-gold px-6 py-3 text-xs font-semibold text-navy transition-colors hover:bg-white">Save Current as Preset</button>
        </div>
        <div className="mt-4 space-y-2">
          {themes.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-navy-800/70 px-4 py-3">
              <p className="flex-1 text-sm font-semibold">{t.name}</p>
              <button data-testid={`preset-apply-${t.id.slice(0, 8)}`} onClick={() => applyPreset(t)} className="rounded-full bg-electric px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-gold hover:text-navy">Apply</button>
              <button data-testid={`preset-delete-${t.id.slice(0, 8)}`} onClick={async () => { if (window.confirm('Delete this preset?')) { await run((c) => c.delete(`${API}/admin/themes/${t.id}`)); loadThemes(); } }} className="rounded-full border border-red-300/40 p-2 text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {themes.length === 0 && <p className="py-2 text-sm text-white/45">No presets saved yet.</p>}
        </div>
      </div>

      <div className="grid max-w-3xl gap-4 rounded-3xl border border-navy/10 bg-white p-6">
        {COLOR_FIELDS.map(([k, label, def]) => (
          <div key={k} className="flex flex-wrap items-center gap-3">
            <div className="min-w-[220px] flex-1">
              <span className={labelCls}>{label}</span>
              <div className="flex items-center gap-2">
                <input data-testid={`design-color-${k}`} type="color" value={form[k] || def} onChange={set(k)} className="h-11 w-14 cursor-pointer rounded-lg border border-navy/15" />
                <input className={`${inputCls} font-mono`} value={form[k]} placeholder={def} onChange={set(k)} />
                {form[k] && <button onClick={() => setForm((f) => ({ ...f, [k]: '' }))} className="text-[11px] font-semibold text-red-500 hover:underline">Reset</button>}
              </div>
            </div>
          </div>
        ))}
        <div className="mt-2 border-t border-navy/10 pt-4">
          <span className={labelCls}>Website font</span>
          <select data-testid="design-font-preset" className={inputCls} value={form.fontPreset} onChange={set('fontPreset')}>
            {FONT_PRESETS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </div>
        <div>
          <span className={labelCls}>Custom font file (upload .ttf/.woff/.woff2 in Media Library — overrides preset)</span>
          <input className={`${inputCls} font-mono`} value={form.customFontUrl} placeholder="/api/uploads/…" onChange={set('customFontUrl')} />
          <MediaPicker adminKey={adminKey} kinds={['font']} onPick={(url) => setForm((f) => ({ ...f, customFontUrl: url }))} testId="design-font-browse" />
        </div>
        <div>
          <span className={labelCls}>Text size</span>
          <select data-testid="design-font-scale" className={inputCls} value={form.fontScale} onChange={set('fontScale')}>
            <option value="">Default</option>
            <option value="compact">Compact</option>
            <option value="large">Large</option>
          </select>
        </div>
        <button data-testid="design-save-button" onClick={save} className="mt-2 w-fit touch-target rounded-full bg-electric px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy">
          {saved ? 'Saved ✓' : 'Save Theme'}
        </button>
        <p className="text-xs text-navy/45">Refresh the public site after saving to see the new theme.</p>
      </div>
    </div>
  );
}

/* ---------------- SEO & Tags ---------------- */
export function SeoSection({ adminKey, onAuthError }) {
  const [form, setForm] = useState({ gtmId: '', customHead: '' });
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(null);
  const run = useAdminFetch(adminKey, onAuthError);
  useEffect(() => { run((c) => c.get(`${API}/settings`)).then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {}); }, [run]);
  const sitemapUrl = `${API}/sitemap.xml`;
  const robotsUrl = `${window.location.origin}/robots.txt`;

  const copy = (key, text) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); };
  const download = async () => {
    const r = await fetch(sitemapUrl);
    const b = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = 'sitemap.xml';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const save = async () => {
    await run((c) => c.put(`${API}/admin/settings`, form));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div data-testid="admin-seo-section">
      <SectionHead title="SEO & Tags" desc="Sitemap tools, Google Tag Manager and custom tracking tags." />
      <div className="max-w-3xl space-y-4">
        <div className="rounded-3xl border border-navy/10 bg-white p-6">
          <span className={labelCls}>Sitemap (updates itself with every published article)</span>
          <p className="break-all rounded-xl bg-paper px-4 py-3 font-mono text-xs text-navy/70">{sitemapUrl}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="sitemap-download" onClick={download} className="flex items-center gap-1.5 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy"><Download className="h-3.5 w-3.5" /> Download XML</button>
            <button data-testid="sitemap-copy" onClick={() => copy('sitemap', sitemapUrl)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy hover:border-electric hover:text-electric">{copied === 'sitemap' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy Link</button>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-navy/10 pt-4">
            <p className="break-all font-mono text-xs text-navy/50">{robotsUrl}</p>
            <button data-testid="robots-copy" onClick={() => copy('robots', robotsUrl)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy hover:border-electric hover:text-electric">{copied === 'robots' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy</button>
          </div>
        </div>

        <div className="rounded-3xl border border-navy/10 bg-white p-6">
          <span className={labelCls}>Google Tag Manager ID</span>
          <input data-testid="gtm-id-input" className={`${inputCls} font-mono`} value={form.gtmId} placeholder="GTM-XXXXXXX" onChange={(e) => setForm((f) => ({ ...f, gtmId: e.target.value }))} />
          <p className="mt-2 text-xs text-navy/45">The GTM snippet is injected on every page automatically once saved.</p>
          <span className={`${labelCls} mt-5`}>Custom head tags (verification meta, analytics, pixels — one tag per line)</span>
          <textarea data-testid="custom-head-input" rows={5} className={`${inputCls} resize-y font-mono text-xs`} value={form.customHead} placeholder={'<meta name="google-site-verification" content="…" />\n<script src="…"></script>'} onChange={(e) => setForm((f) => ({ ...f, customHead: e.target.value }))} />
          <button data-testid="tags-save-button" onClick={save} className="mt-4 touch-target rounded-full bg-electric px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy">
            {saved ? 'Saved ✓' : 'Save Tags'}
          </button>
        </div>
      </div>
    </div>
  );
}
